import { Link } from "react-router-dom";
import Icon from "../Icon/Icon";
import styles from "./NotificationsList.module.css";
import type { IGetNotificationsResponse } from "../../shared/types/notifications.types";
import { format } from "date-fns";
import api from "../../api/api";
import { useContext, useEffect, useState, useRef, useCallback } from "react";
import { NotificationsContext } from "../../shared/Contexts/Notifications/NotificationsContext";
import ConfirmActionModal from "../ConfirmActionModal/ConfirmActionModal";
import SelectElement, {
  type SelectListItemType,
} from "../SelectElement/SelectElement";

type FilterLiteral = "Todos" | "Leídos" | "No Leídos";

const NotificationsList = () => {
  const { revalidateNotifications } = useContext(NotificationsContext);
  const [activeFilter, setActiveFilter] = useState<FilterLiteral>("Todos");
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [mergedNotifications, setMergedNotifications] =
    useState<IGetNotificationsResponse | null>(null);
  const limit = 15;
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const [selectedNotificationType, setSelectedNotificationType] =
    useState<SelectListItemType>({
      id: "all",
      element: <p className={styles.selectListItem}>Tipo de notificación</p>,
      name: "Tipo de notificació",
    });

  const scrollRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const notificationsTypesStatic = [
    "Documento NO Aprobado",
    "Información faltante",
    "Verificar Documento",
    "Verificación Manual",
  ];

  const getNotificationMessage = (
    notification: IGetNotificationsResponse["data"][0]
  ) => {
    switch (notification.type) {
      case "LOW_CONFIDENCE":
        return "Documento NO Aprobado";
      case "MISSING_PROPERTIES":
        return "Información faltante";
      case "PROCESSING_COMPLETE":
        return "Verificar Documento";
      case "VALIDATION_REQUIRED":
        return "Verificación Manual";
      default:
        return "";
    }
  };

  const getNotificationType = (notification: string | undefined) => {
    switch (notification) {
      case "Documento NO Aprobado":
        return "LOW_CONFIDENCE";
      case "Información faltante":
        return "MISSING_PROPERTIES";
      case "Verificar Documento":
        return "PROCESSING_COMPLETE";
      case "Verificación Manual":
        return "VALIDATION_REQUIRED";
      default:
        return "";
    }
  };

  const getNotificationIconId = (
    notification: IGetNotificationsResponse["data"][0]
  ) => {
    switch (notification.type) {
      case "LOW_CONFIDENCE":
        return "document-failure";
      case "MISSING_PROPERTIES":
        return "document-missing-mandatory-fields";
      case "PROCESSING_COMPLETE":
        return "document-verify";
      case "VALIDATION_REQUIRED":
        return "document-manual-validation";
      default:
        return "document-failure";
    }
  };
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    const correctType = getNotificationType(selectedNotificationType?.name);

    try {
      const response = await revalidateNotifications({
        page,
        limit,
        type: correctType !== "" ? correctType : undefined,
      });
      if (!response) return;

      setMergedNotifications((prev) => {
        if (!prev || page === 1) return response;

        const existingIds = new Set(prev.data.map((n) => n.id));
        const newData = response.data.filter((n) => !existingIds.has(n.id));
        return {
          ...response,
          data: [...prev.data, ...newData],
        };
      });

      setHasMore(response.totalPages > page);
    } catch (error) {
      console.error("Ошибка при загрузке уведомлений", error);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [page, selectedNotificationType?.name, revalidateNotifications]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el || loadingRef.current || !hasMore) return;

    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight;
    const clientHeight = el.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight - 50) {
      loadingRef.current = true;
      setPage((prev) => prev + 1);
    }
  };

  const handleReadAll = async () => {
    await api.markAllNotificationsAsRead();
    fetchNotifications();
    setPage(1);
  };

  const handleReadOne = async (id: string) => {
    await api.updateNotification({ id, isRead: true });
    revalidateNotifications({});
    setPage(1);
    setMergedNotifications(null);
  };

  const filteredNotifications =
    mergedNotifications?.data.filter((notification) => {
      if (activeFilter === "Todos") return true;
      if (activeFilter === "Leídos") return notification.isRead === true;
      if (activeFilter === "No Leídos") return notification.isRead === false;
      return true;
    }) ?? [];

  const selectType = (option: {
    id: string;
    element: React.ReactNode;
    name?: string;
  }) => {
    setSelectedNotificationType(option);
    setMergedNotifications(null);
    setPage(1);
  };

  return (
    <>
      <div className={styles.notificationsContainer}>
        <div className={styles.headContainer}>
          <h2 className={styles.title}>Centro de Revisión</h2>
          <button
            className={styles.readAllButton}
            onClick={() => setIsConfirmModalOpen(true)}
          >
            Marcar como leído <Icon name="read-all" />
          </button>
        </div>
        <ul className={styles.filtersContainer}>
          {(["Todos", "Leídos", "No Leídos"] as FilterLiteral[]).map(
            (filter) => (
              <li
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={
                  activeFilter === filter ? styles.activeFilter : styles.filter
                }
              >
                {filter}
              </li>
            )
          )}
        </ul>
        <SelectElement
          options={notificationsTypesStatic.map((type, index) => ({
            id: `${index + 1}`,
            element: <p className={styles.selectListItem}>{type}</p>,
            name: type,
          }))}
          setSelected={selectType}
          selected={selectedNotificationType}
          className={styles.selectElement}
          dropdownItemClassName={styles.selectElementDropdownItem}
          dropdownClassName={styles.selectElementDropdown}
        />
        <div
          className={styles.notificationsListWrapper}
          ref={scrollRef}
          onScroll={onScroll}
        >
          <ul className={styles.notificationsList}>
            {filteredNotifications.map((notification) => (
              <li
                key={notification.id}
                onClick={() => handleReadOne(notification.id)}
              >
                <Link
                  className={
                    notification.isRead
                      ? styles.notification
                      : styles.unreadNotification
                  }
                  to={`/review-document/${notification.document.id}`}
                >
                  <Icon
                    name={getNotificationIconId(notification)}
                    size={50}
                    className={styles.notificationIcon}
                  />
                  <div className={styles.notificationTextWrapper}>
                    <p className={styles.notificationMessage}>
                      {getNotificationMessage(notification)}
                      <span className={styles.notificationDate}>
                        <span className={styles.notificationDateTitle}>
                          Fecha:
                        </span>{" "}
                        {format(new Date(notification.createdAt), "dd/MM/yy")}
                      </span>
                    </p>
                    <p className={styles.notificationInfo}>
                      <span className={styles.notificationDataTitle}>
                        Tipo de Documento
                      </span>
                      <span
                        title={notification.document?.type?.name || "None"}
                        className={styles.notificationDataText}
                      >
                        {notification.document?.type?.name || "None"}
                      </span>
                    </p>
                    <p className={styles.notificationDataTitle}>
                      Enviado por{" "}
                      <span
                        title={notification.userId}
                        className={styles.notificationDataText}
                      >
                        {`Usuario ${notification.userId}`}
                      </span>
                    </p>
                  </div>
                </Link>
              </li>
            ))}
            {loading && <p className={styles.loadingText}>Cargando...</p>}
          </ul>
        </div>
      </div>
      <ConfirmActionModal
        isOpen={isConfirmModalOpen}
        onRequestClose={() => setIsConfirmModalOpen(false)}
        modalTitle={`Marcar ${
          mergedNotifications?.unreadCount ?? 0
        } notificaciones como leídas`}
        modalText="¿Está seguro que desea marcar estas notificaciones como leídas? Seguirán estando en el centro de revisión."
        cancelActionText="Cancelar"
        cancelActionHandler={() => setIsConfirmModalOpen(false)}
        confirmActionText="Aceptar"
        confirmActionHandler={() => {
          handleReadAll();
          setIsConfirmModalOpen(false);
        }}
      />
    </>
  );
};

export default NotificationsList;
